/**
 * CalendarSyncAuditService
 * 
 * Persists audit entries for calendar sync operations to Supabase.
 * This provides observability into push/pull operations for debugging
 * and monitoring purposes.
 * 
 * Design Decisions:
 * - Uses batch inserts for efficiency
 * - Fire-and-forget: audit failures don't block sync operations
 * - Logs errors but doesn't throw to avoid disrupting sync flow
 */

import { supabase, isDemoMode } from '@/lib/supabase';
import type { SyncEventResult, PullEventResult } from './GoogleCalendarSyncService';
import type { OutlookSyncResult } from './OutlookCalendarService';

// ─────────────────────────────────────────────────────────────────────────────
// Helper to get supabase client
// ─────────────────────────────────────────────────────────────────────────────

function getClient() {
  if (isDemoMode || !supabase) {
    return null;
  }
  return supabase;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Calendar provider type */
export type AuditProvider = 'google' | 'microsoft';

/** Push operation types */
export type PushOperation = 'push:create' | 'push:update' | 'push:skip' | 'push:error';

/** Pull operation types */
export type PullOperation = 'pull:update' | 'pull:cancel' | 'pull:skip' | 'pull:conflict' | 'pull:error';

/** All operation types */
export type AuditOperation = PushOperation | PullOperation;

/** Audit entry status */
export type AuditStatus = 'success' | 'error' | 'skipped' | 'conflict';

/**
 * Audit entry to be inserted into the database.
 */
export interface CalendarSyncAuditEntry {
  /** User who triggered the sync */
  userId: string;
  
  /** Calendar provider */
  provider: AuditProvider;
  
  /** Operation type */
  operation: AuditOperation;
  
  /** AX1 event ID (if applicable) */
  ax1EventId?: string;
  
  /** External event ID (Google/Outlook) */
  externalEventId?: string;
  
  /** Operation status */
  status: AuditStatus;
  
  /** Human-readable message */
  message?: string;
  
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Database row shape for calendar_sync_audit table.
 */
interface AuditRow {
  user_id: string;
  provider: AuditProvider;
  operation: AuditOperation;
  ax1_event_id?: string;
  external_event_id?: string;
  status: AuditStatus;
  message?: string;
  metadata?: Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps SyncEventResult operation to push operation type.
 */
function mapPushOperation(operation: SyncEventResult['operation']): PushOperation {
  switch (operation) {
    case 'created':
      return 'push:create';
    case 'updated':
      return 'push:update';
    case 'skipped':
      return 'push:skip';
    case 'error':
      return 'push:error';
    default:
      return 'push:error';
  }
}

/**
 * Maps SyncEventResult operation to audit status.
 */
function mapPushStatus(operation: SyncEventResult['operation']): AuditStatus {
  switch (operation) {
    case 'created':
    case 'updated':
      return 'success';
    case 'skipped':
      return 'skipped';
    case 'error':
      return 'error';
    default:
      return 'error';
  }
}

/**
 * Maps PullEventResult operation to pull operation type.
 */
function mapPullOperation(operation: PullEventResult['operation']): PullOperation {
  switch (operation) {
    case 'updated':
      return 'pull:update';
    case 'cancelled':
      return 'pull:cancel';
    case 'skipped':
      return 'pull:skip';
    case 'conflict':
      return 'pull:conflict';
    case 'error':
      return 'pull:error';
    default:
      return 'pull:error';
  }
}

/**
 * Maps PullEventResult operation to audit status.
 */
function mapPullStatus(operation: PullEventResult['operation']): AuditStatus {
  switch (operation) {
    case 'updated':
    case 'cancelled':
      return 'success';
    case 'skipped':
      return 'skipped';
    case 'conflict':
      return 'conflict';
    case 'error':
      return 'error';
    default:
      return 'error';
  }
}

/**
 * Converts CalendarSyncAuditEntry to database row format.
 */
function toRow(entry: CalendarSyncAuditEntry): AuditRow {
  return {
    user_id: entry.userId,
    provider: entry.provider,
    operation: entry.operation,
    ax1_event_id: entry.ax1EventId,
    external_event_id: entry.externalEventId,
    status: entry.status,
    message: entry.message,
    metadata: entry.metadata,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Service Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Inserts multiple audit entries in a batch.
 * This is fire-and-forget: errors are logged but don't throw.
 * 
 * @param entries - Array of audit entries to insert
 * @returns Promise resolving to the number of rows inserted, or 0 on error
 */
export async function insertAuditEntries(entries: CalendarSyncAuditEntry[]): Promise<number> {
  if (entries.length === 0) {
    return 0;
  }
  
  const client = getClient();
  if (!client) {
    console.log('[CalendarSyncAudit] Demo mode or no client - skipping audit insert');
    return 0;
  }
  
  const rows = entries.map(toRow);
  
  try {
    const { error, count } = await client
      .from('calendar_sync_audit')
      .insert(rows)
      .select('id');
    
    if (error) {
      console.error('[CalendarSyncAudit] Failed to insert audit entries:', error);
      return 0;
    }
    
    console.log(`[CalendarSyncAudit] Inserted ${count ?? rows.length} audit entries`);
    return count ?? rows.length;
  } catch (err) {
    console.error('[CalendarSyncAudit] Exception inserting audit entries:', err);
    return 0;
  }
}

/**
 * Creates audit entries from Google push results (SyncEventResult[]).
 * 
 * @param userId - User who triggered the sync
 * @param results - Array of SyncEventResult from push operation
 * @returns Array of CalendarSyncAuditEntry ready for insertion
 */
export function createGooglePushAuditEntries(
  userId: string,
  results: SyncEventResult[]
): CalendarSyncAuditEntry[] {
  return results.map((result) => ({
    userId,
    provider: 'google' as const,
    operation: mapPushOperation(result.operation),
    ax1EventId: result.ax1EventId,
    externalEventId: result.googleEventId,
    status: mapPushStatus(result.operation),
    message: result.message,
    metadata: {
      timestamp: result.timestamp,
      syncInfo: result.syncInfo,
    },
  }));
}

/**
 * Creates audit entries from Google pull results (PullEventResult[]).
 * 
 * @param userId - User who triggered the sync
 * @param results - Array of PullEventResult from pull operation
 * @returns Array of CalendarSyncAuditEntry ready for insertion
 */
export function createGooglePullAuditEntries(
  userId: string,
  results: PullEventResult[]
): CalendarSyncAuditEntry[] {
  return results.map((result) => ({
    userId,
    provider: 'google' as const,
    operation: mapPullOperation(result.operation),
    ax1EventId: result.ax1EventId,
    externalEventId: result.googleEventId,
    status: mapPullStatus(result.operation),
    message: result.message,
    metadata: {
      timestamp: result.timestamp,
      updatedFields: result.updatedFields,
      hasConflicts: (result.conflicts?.length ?? 0) > 0,
      conflictCount: result.conflicts?.length,
    },
  }));
}

/**
 * Creates audit entries from Microsoft/Outlook push results (OutlookSyncResult[]).
 * 
 * @param userId - User who triggered the sync
 * @param results - Array of OutlookSyncResult from push operation
 * @returns Array of CalendarSyncAuditEntry ready for insertion
 */
export function createMicrosoftPushAuditEntries(
  userId: string,
  results: OutlookSyncResult[]
): CalendarSyncAuditEntry[] {
  return results.map((result) => ({
    userId,
    provider: 'microsoft' as const,
    operation: mapPushOperation(result.operation),
    ax1EventId: result.ax1EventId,
    externalEventId: result.outlookEventId,
    status: mapPushStatus(result.operation),
    message: result.message,
    metadata: {
      timestamp: result.timestamp,
      syncInfo: result.syncInfo,
    },
  }));
}

/**
 * Convenience function to audit Google push results.
 * Fire-and-forget: returns immediately after queueing the insert.
 * 
 * @param userId - User who triggered the sync
 * @param results - Push operation results
 */
export function auditGooglePush(userId: string, results: SyncEventResult[]): void {
  const entries = createGooglePushAuditEntries(userId, results);
  // Fire-and-forget: don't await
  insertAuditEntries(entries).catch((err) => {
    console.error('[CalendarSyncAudit] Async audit error (push):', err);
  });
}

/**
 * Convenience function to audit Google pull results.
 * Fire-and-forget: returns immediately after queueing the insert.
 * 
 * @param userId - User who triggered the sync
 * @param results - Pull operation results
 */
export function auditGooglePull(userId: string, results: PullEventResult[]): void {
  const entries = createGooglePullAuditEntries(userId, results);
  // Fire-and-forget: don't await
  insertAuditEntries(entries).catch((err) => {
    console.error('[CalendarSyncAudit] Async audit error (pull):', err);
  });
}

/**
 * Convenience function to audit Microsoft push results.
 * Fire-and-forget: returns immediately after queueing the insert.
 * 
 * @param userId - User who triggered the sync
 * @param results - Push operation results
 */
export function auditMicrosoftPush(userId: string, results: OutlookSyncResult[]): void {
  const entries = createMicrosoftPushAuditEntries(userId, results);
  // Fire-and-forget: don't await
  insertAuditEntries(entries).catch((err) => {
    console.error('[CalendarSyncAudit] Async audit error (microsoft push):', err);
  });
}
